import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { darkTheme, FONT, FONT_MONO } from '../../theme/tokens';
import { boardCanvasView, type BoardResponse, type CanvasView } from '../../types';
import { useBoards, useDeleteBoard } from '../../api/hooks';
import {
  DEFAULT_WORKSPACE_SHELL,
  effectiveBoardsInUse,
  useWorkspaceShellStore,
  type SelectableCoreView,
} from '../../store/workspaceShellStore';
import { useAppStore } from '../../store/appStore';
import { CANVAS_NAV_SECTIONS } from './canvasNav';
import { SpecThumbnail } from '../boards/SpecThumbnail';
import { CORE_THUMB_DESCS, specThumbDescs } from '../boards/specThumbs';
import { requestChatFocus } from '../agent/chatFocus';
import { formatValue } from '../boards/binder';

const t = darkTheme;

// --- The View Library (SC-2873) — one door for workspace management ------------
//
// Every view available to this workspace on one shelf: Core views (built-in;
// removable from the bar, never deletable), Templates (instantiate per
// catalog), Created here (agent-authored; full lifecycle incl. delete). The
// tab bar is a SELECTION from this library — "Remove" always returns a view to
// its shelf, a workspace preference, not a deletion. Data surfaces stay calm;
// management lives behind this one door.

interface ViewLibraryProps {
  open: boolean;
  onClose: () => void;
  catalogId: string;
  /** The entities anchor's display label (the catalog's entity term). */
  entityLabel: string;
  /** The catalog's humanized vertical for the Templates shelf heading. */
  verticalLabel: string | null;
  activeView: CanvasView;
}

const chipStyle = (bg: string, color: string): CSSProperties => ({
  borderRadius: 99,
  padding: '2px 9px',
  fontSize: 10,
  fontWeight: 700,
  flexShrink: 0,
  background: bg,
  color,
});

function actionButtonStyle(danger = false): CSSProperties {
  return {
    border: `1px solid ${t.border2}`,
    background: t.surface,
    color: danger ? t.redText : t.text2,
    borderRadius: 8,
    padding: '5px 11px',
    fontSize: 11.5,
    fontWeight: 600,
    fontFamily: FONT,
    cursor: 'pointer',
  };
}

const disabledButtonStyle: CSSProperties = {
  ...actionButtonStyle(),
  opacity: 0.45,
  cursor: 'default',
};

function shelfHeading(text: string) {
  return (
    <h4
      style={{
        margin: '0 0 4px',
        fontFamily: FONT_MONO,
        fontSize: 9.5,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: t.text3,
        fontWeight: 500,
      }}
    >
      {text}
    </h4>
  );
}

interface LibraryRowProps {
  thumb: ReactNode;
  name: string;
  sub: string;
  chip: ReactNode;
  actions: ReactNode;
  last?: boolean;
}

function LibraryRow({ thumb, name, sub, chip, actions, last }: LibraryRowProps) {
  return (
    <div
      data-testid="library-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 0',
        borderBottom: last ? 'none' : `1px solid ${t.border}`,
      }}
    >
      {thumb}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, color: t.text, fontWeight: 600 }}>{name}</div>
        <div style={{ fontSize: 11.5, color: t.text3, marginTop: 1 }}>{sub}</div>
      </div>
      {chip}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>{actions}</div>
    </div>
  );
}

/** A created board's subline: who built it, when. */
function createdSub(board: BoardResponse): string {
  const when = formatValue(board.created_at, 'date');
  const who = board.created_by?.name;
  return who ? `Built with ${who} · ${when}` : `Built in chat · ${when}`;
}

export function ViewLibrary({
  open,
  onClose,
  catalogId,
  entityLabel,
  verticalLabel,
  activeView,
}: ViewLibraryProps) {
  const boards = useBoards(catalogId);
  const shell = useWorkspaceShellStore((s) => s.byCatalog[catalogId] ?? DEFAULT_WORKSPACE_SHELL);
  const { addCoreView, removeCoreView, addBoard, removeBoard } =
    useWorkspaceShellStore.getState();
  const setChatCollapsed = useAppStore((s) => s.setChatCollapsed);
  const deleteBoard = useDeleteBoard(catalogId);

  // Two-step delete: the first click arms the row, the second confirms.
  const [deleteArmedId, setDeleteArmedId] = useState<string | null>(null);
  // The overlay-local toast — copy is the feature here ("nothing was deleted").
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (toast === null) return;
    const timer = setTimeout(() => setToast(null), 3400);
    return () => clearTimeout(timer);
  }, [toast]);

  // Closing (✕ / scrim / Esc / the CTA) also drops the transient state — a
  // re-open starts clean, no armed delete, no stale toast. Done in the close
  // handler, not an open-keyed effect (react-hooks/set-state-in-effect).
  const close = useCallback(() => {
    setDeleteArmedId(null);
    setToast(null);
    onClose();
  }, [onClose]);

  // The chat CTA hands focus to the composer — the close-time opener-restore
  // below must stand down for that one path, or it would immediately steal the
  // focus back to the `+` (Codex review on this PR).
  const skipOpenerRestoreRef = useRef(false);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    // Move focus into the dialog; hand it back to the opener (the `+`) when
    // the library closes. Depends on `open` alone — WorkspaceShell passes a
    // stable onClose, and re-running this on a handler-identity change would
    // bounce focus mid-open.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (!skipOpenerRestoreRef.current) opener?.focus();
      skipOpenerRestoreRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `close` is stable per open cycle; keying on it would re-run the focus handoff mid-open
  }, [open]);

  if (!open) return null;

  const allBoards = boards.data ?? [];
  const templates = allBoards.filter((b) => !b.created_by_agent);
  const created = allBoards.filter((b) => b.created_by_agent);

  const coreRow = (view: SelectableCoreView, label: string, last: boolean) => {
    const inUse = shell.coreViewsInUse.includes(view);
    return (
      <LibraryRow
        key={view}
        thumb={<SpecThumbnail descs={CORE_THUMB_DESCS[view]} />}
        name={label}
        sub={inUse ? 'On the tab bar' : 'In the library — not shown'}
        chip={<span style={chipStyle(t.surfaceAlt, t.text2)}>Built-in</span>}
        last={last}
        actions={
          inUse ? (
            <button
              type="button"
              style={actionButtonStyle()}
              onClick={() => {
                removeCoreView(catalogId, view);
                setToast(`${label} returned to the library — nothing was deleted`);
              }}
            >
              − Remove
            </button>
          ) : (
            <button
              type="button"
              style={actionButtonStyle()}
              onClick={() => addCoreView(catalogId, view)}
            >
              + Add
            </button>
          )
        }
      />
    );
  };

  const boardRow = (board: BoardResponse, shelf: 'template' | 'created', last: boolean) => {
    // The SAME effective-pins predicate the tab bar uses (workspaceShellStore):
    // a `required` org board is on the bar whether or not it was ever manually
    // pinned, so this row must read "Required", never "+ Add".
    const inUse = effectiveBoardsInUse(shell, allBoards).includes(board.id);
    const viewing = activeView === boardCanvasView(board.id);
    const armed = deleteArmedId === board.id;
    const deleting = deleteBoard.isPending && deleteBoard.variables === board.id;
    const actions: ReactNode[] = [];

    if (armed || deleting) {
      // The row stays in its confirm state (disabled) until the mutation
      // settles — clearing it early would restore a live Delete over the
      // still-cached row and invite a duplicate request whose 404 would then
      // report a failure for a board that IS deleted.
      actions.push(
        <button
          key="confirm"
          type="button"
          disabled={deleting}
          style={{
            ...actionButtonStyle(true),
            borderColor: t.red,
            ...(deleting ? { opacity: 0.45, cursor: 'default' } : {}),
          }}
          onClick={() => {
            if (deleteBoard.isPending) return; // double-click before the re-render
            deleteBoard.mutate(board.id, {
              onSuccess: () => {
                setDeleteArmedId(null);
                removeBoard(catalogId, board.id);
                setToast(`${board.title} deleted — ask the agent again anytime`);
              },
              onError: () => {
                setDeleteArmedId(null);
                setToast(`Couldn't delete ${board.title} — try again`);
              },
            });
          }}
        >
          {deleting ? 'Deleting…' : 'Delete this view'}
        </button>,
        <button
          key="cancel"
          type="button"
          disabled={deleting}
          style={deleting ? disabledButtonStyle : actionButtonStyle()}
          onClick={() => setDeleteArmedId(null)}
        >
          Cancel
        </button>,
      );
    } else {
      if (inUse) {
        if (viewing) {
          actions.push(
            <button key="viewing" type="button" disabled style={disabledButtonStyle}>
              In use — viewing
            </button>,
          );
        } else if (board.required) {
          actions.push(
            <button
              key="required"
              type="button"
              disabled
              title="Your organization keeps this view on everyone's workspace"
              style={disabledButtonStyle}
            >
              Required
            </button>,
          );
        } else {
          actions.push(
            <button
              key="remove"
              type="button"
              style={actionButtonStyle()}
              onClick={() => {
                removeBoard(catalogId, board.id);
                setToast(`${board.title} returned to the library — nothing was deleted`);
              }}
            >
              − Remove
            </button>,
          );
        }
      } else {
        actions.push(
          <button
            key="add"
            type="button"
            style={actionButtonStyle()}
            onClick={() => addBoard(catalogId, board.id)}
          >
            + Add
          </button>,
        );
      }
      if (shelf === 'created') {
        actions.push(
          <button
            key="delete"
            type="button"
            style={actionButtonStyle(true)}
            onClick={() => {
              if (viewing) {
                setToast('Switch views before deleting the one you have open');
                return;
              }
              setDeleteArmedId(board.id);
            }}
          >
            Delete
          </button>,
        );
      }
    }

    return (
      <LibraryRow
        key={board.id}
        thumb={<SpecThumbnail descs={specThumbDescs(board.spec)} />}
        name={board.title}
        sub={
          shelf === 'created'
            ? createdSub(board)
            : inUse
              ? 'On the tab bar'
              : 'Ready to add to this workspace'
        }
        chip={
          shelf === 'created' ? (
            <span style={chipStyle(t.accentBg, t.accent)}>Created here</span>
          ) : (
            <span style={chipStyle(t.blueBg, t.blueText)}>EKOM template</span>
          )
        }
        actions={actions}
        last={last}
      />
    );
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        // Above the canvas detail overlays (CanvasDetailOverlay sits at 1000):
        // the tabs stay interactive while a detail is open, so the library can
        // legitimately be summoned OVER one and must cover it.
        zIndex: 1100,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '64px 20px 20px',
        fontFamily: FONT,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-label="View library"
        tabIndex={-1}
        style={{
          width: 640,
          maxWidth: '94vw',
          maxHeight: 'calc(100vh - 96px)',
          overflow: 'auto',
          background: t.bgAlt,
          border: `1px solid ${t.border2}`,
          borderRadius: 14,
          outline: 'none',
          boxShadow: '0 24px 70px rgba(0,0,0,0.55)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 22px 4px' }}>
          <b style={{ fontSize: 16, color: t.text }}>View library</b>
          <button
            type="button"
            aria-label="Close the library"
            onClick={close}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: t.text3,
              fontSize: 16,
              cursor: 'pointer',
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Core views */}
        <div style={{ padding: '12px 22px 6px' }}>
          {shelfHeading('Core views')}
          <LibraryRow
            thumb={<SpecThumbnail descs={CORE_THUMB_DESCS.entities} />}
            name={entityLabel}
            sub="The home surface — always on"
            chip={<span style={chipStyle(t.surfaceAlt, t.text2)}>Built-in</span>}
            actions={
              <button type="button" disabled style={disabledButtonStyle}>
                Always on
              </button>
            }
          />
          {CANVAS_NAV_SECTIONS.filter(
            (s): s is (typeof CANVAS_NAV_SECTIONS)[number] & { view: SelectableCoreView } =>
              s.view !== 'entities',
          ).map((section, i, arr) =>
            coreRow(section.view, section.label ?? entityLabel, i === arr.length - 1),
          )}
        </div>

        {/* Templates */}
        <div style={{ padding: '12px 22px 6px' }}>
          {shelfHeading(verticalLabel ? `Templates · ${verticalLabel}` : 'Templates')}
          {boards.isPending ? (
            <div style={{ fontSize: 11.5, color: t.text3, padding: '9px 0' }}>Loading views…</div>
          ) : boards.isError ? (
            // An honest retry: the query observer stays mounted across
            // close/reopen, so "reopen to try again" would never refetch.
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 11.5,
                color: t.amberText,
                padding: '9px 0',
              }}
            >
              <span>The view shelves couldn't load.</span>
              <button
                type="button"
                onClick={() => {
                  void boards.refetch();
                }}
                style={{ ...actionButtonStyle(), padding: '3px 9px' }}
              >
                Try again
              </button>
            </div>
          ) : templates.length > 0 ? (
            templates.map((b, i) => boardRow(b, 'template', i === templates.length - 1))
          ) : (
            <div style={{ fontSize: 11.5, color: t.text3, padding: '9px 0' }}>
              No templates for this catalog yet.
            </div>
          )}
        </div>

        {/* Created here */}
        <div style={{ padding: '12px 22px 6px' }}>
          {shelfHeading('Created here')}
          {boards.isPending || boards.isError ? null : created.length > 0 ? (
            created.map((b, i) => boardRow(b, 'created', i === created.length - 1))
          ) : (
            <div style={{ fontSize: 11.5, color: t.text3, padding: '9px 0' }}>
              Nothing yet — anything the agent builds for you lands on this shelf.
            </div>
          )}
        </div>

        {/* Ask-the-agent CTA */}
        <button
          type="button"
          onClick={() => {
            // Focus is HANDED OFF to the composer — the opener-restore must not
            // steal it back on this close path.
            skipOpenerRestoreRef.current = true;
            close();
            setChatCollapsed(false);
            // Deferred past the close commit: while the library is open the
            // whole workspace (composer included) is `inert`, and a focus()
            // into an inert subtree is a NO-OP in real browsers (caught by the
            // Playwright pass — jsdom doesn't enforce inert). rAF lands after
            // the commit that lifts it.
            requestAnimationFrame(() => requestChatFocus());
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            margin: '10px 22px 18px',
            padding: '12px 14px',
            border: `1px dashed ${t.border2}`,
            borderRadius: 10,
            background: 'none',
            width: 'calc(100% - 44px)',
            color: t.text2,
            fontSize: 13,
            fontFamily: FONT,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <span aria-hidden style={{ color: t.accent }}>
            ✦
          </span>
          <span>
            Ask the agent to build a new view — describe it in chat, preview, then add it here
          </span>
        </button>

        <div style={{ padding: '0 22px 16px', fontSize: 11.5, color: t.text3, lineHeight: 1.5 }}>
          Removing returns a view to this shelf — only views created here can be deleted. Core
          views are maintained by EKOM and always available.
        </div>

        {toast !== null && (
          <div
            role="status"
            style={{
              position: 'sticky',
              bottom: 12,
              margin: '0 22px 12px',
              background: t.surface,
              border: `1px solid ${t.accentBorder}`,
              color: t.text,
              borderRadius: 10,
              padding: '10px 16px',
              fontSize: 13,
            }}
          >
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}
